import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function AIEmailComposer({ open, onClose, lead, onSent }) {
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [goal, setGoal] = useState("introduce Alpine Straits and book a discovery call");

  const generate = async () => {
    if (!lead) return;
    setGenerating(true);
    try {
      const res = await api.post("/ai/draft-email", { lead_id: lead.lead_id, tone: "professional", goal });
      setSubject(res.data.subject);
      setHtml(res.data.html);
      toast.success("Draft ready");
    } catch (_e) {
      toast.error("Claude draft failed");
    } finally { setGenerating(false); }
  };

  const send = async () => {
    if (!subject || !html) { toast.error("Subject and body required"); return; }
    setSending(true);
    try {
      await api.post("/email/send", { lead_id: lead.lead_id, subject, html_content: html });
      toast.success(`Sent to ${lead.email}`);
      onSent?.();
      onClose();
      setSubject(""); setHtml("");
    } catch (_e) {
      toast.error("Send failed");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ai-email-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C5A880]" /> Compose outreach — {lead?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Objective</Label>
            <Input data-testid="ai-goal" value={goal} onChange={(e) => setGoal(e.target.value)} />
          </div>
          <Button
            data-testid="ai-generate-btn"
            onClick={generate}
            disabled={generating}
            className="bg-[#C5A880] text-[#0A1128] hover:bg-[#D4C09E]"
          >
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting with Claude…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate with Claude Sonnet 5</>}
          </Button>

          <div>
            <Label>Subject</Label>
            <Input data-testid="ai-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Will be filled by AI…" />
          </div>
          <div>
            <Label>Body (HTML)</Label>
            <Textarea data-testid="ai-html" value={html} onChange={(e) => setHtml(e.target.value)} rows={10} placeholder="Will be filled by AI…" className="font-mono text-xs" />
          </div>
          {html && (
            <div className="border border-[#E2E8F0] rounded-md p-4 bg-[#F9FAFB]">
              <div className="label-eyebrow mb-2">Preview</div>
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button data-testid="ai-send-btn" onClick={send} disabled={sending || !subject || !html} className="bg-[#0A1128] text-white hover:bg-[#152243]">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send to {lead?.email}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
